import AsyncStorage from '@react-native-async-storage/async-storage';
import { supabase } from '@/lib/supabase';

const PROGRAMS_KEY = '@duel_coach_programs';

export interface ProgramExercise {
  id: string;
  exerciseName: string;
  targetSets: number;
  targetReps: number;
  targetWeight?: number;
  rpe?: number;
  notes?: string;
}

export interface ProgramDay {
  id: string;
  name: string;
  exercises: ProgramExercise[];
}

export interface Program {
  id: string;
  name: string;
  days: ProgramDay[];
  createdAt: string;
  coachId: string;
  assignedTo?: string[];
}

// ---------------------------------------------------------------------------
// AsyncStorage fallback helpers
// ---------------------------------------------------------------------------

async function loadAllProgramsLocal(): Promise<Program[]> {
  try {
    const raw = await AsyncStorage.getItem(PROGRAMS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

async function saveAllProgramsLocal(programs: Program[]): Promise<void> {
  await AsyncStorage.setItem(PROGRAMS_KEY, JSON.stringify(programs));
}

// ---------------------------------------------------------------------------
// Supabase-first helpers
// ---------------------------------------------------------------------------

function rowToProgram(row: any): Program {
  return {
    id: row.id,
    name: row.name,
    days: (row.days ?? []) as ProgramDay[],
    createdAt: row.created_at,
    coachId: row.coach_id,
  };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export async function saveProgram(program: Program): Promise<void> {
  // Supabase upsert
  const { error } = await supabase.from('coach_programs').upsert({
    id: program.id,
    coach_id: program.coachId,
    name: program.name,
    description: '',
    days: program.days as any,
    updated_at: new Date().toISOString(),
  });

  if (error) {
    console.warn('[programBuilder] Supabase upsert failed, falling back to AsyncStorage', error.message);
    const all = await loadAllProgramsLocal();
    const idx = all.findIndex((p) => p.id === program.id);
    if (idx >= 0) all[idx] = program;
    else all.push(program);
    await saveAllProgramsLocal(all);
    return;
  }

  // Mirror to AsyncStorage for offline access
  const all = await loadAllProgramsLocal();
  const idx = all.findIndex((p) => p.id === program.id);
  if (idx >= 0) all[idx] = program;
  else all.push(program);
  await saveAllProgramsLocal(all);
}

export async function deleteProgram(programId: string): Promise<void> {
  const { error } = await supabase.from('coach_programs').delete().eq('id', programId);

  if (error) {
    console.warn('[programBuilder] Supabase delete failed, falling back to AsyncStorage', error.message);
  }

  // Always clean local cache
  const all = await loadAllProgramsLocal();
  await saveAllProgramsLocal(all.filter((p) => p.id !== programId));
}

export async function loadPrograms(coachId: string): Promise<Program[]> {
  const { data, error } = await supabase
    .from('coach_programs')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: false });

  if (error || !data) {
    console.warn('[programBuilder] Supabase load failed, falling back to AsyncStorage', error?.message);
    const all = await loadAllProgramsLocal();
    return all.filter((p) => p.coachId === coachId);
  }

  const programs = data.map(rowToProgram);

  // Cache locally
  const otherPrograms = (await loadAllProgramsLocal()).filter((p) => p.coachId !== coachId);
  await saveAllProgramsLocal([...otherPrograms, ...programs]);

  return programs;
}

/**
 * Get all programs assigned to a specific client.
 * Used by the client to retrieve their coach's assigned programs.
 */
export async function getAssignedPrograms(clientId: string): Promise<Program[]> {
  try {
    const { data, error } = await supabase
      .from('program_assignments')
      .select('program_id, status, programs(*)')
      .eq('client_id', clientId)
      .eq('status', 'active');

    if (!error && data && data.length > 0) {
      return data
        .map((row: any) => row.programs)
        .filter(Boolean) as Program[];
    }
  } catch {
    // fall back to local
  }

  // AsyncStorage fallback: find programs where assignedTo includes clientId
  const all = await loadAllProgramsLocal();
  return all.filter((p) => p.assignedTo?.includes(clientId));
}

export async function assignProgram(programId: string, clientId: string): Promise<void> {
  const { error } = await supabase.from('program_assignments').insert({
    program_id: programId,
    client_id: clientId,
    status: 'active',
  });

  if (error) {
    console.warn('[programBuilder] Supabase assign failed, falling back to AsyncStorage', error.message);
    // Offline fallback – track in local program object
    const all = await loadAllProgramsLocal();
    const program = all.find((p) => p.id === programId);
    if (!program) return;
    if (!program.assignedTo) program.assignedTo = [];
    if (!program.assignedTo.includes(clientId)) {
      program.assignedTo.push(clientId);
    }
    await saveAllProgramsLocal(all);
  }
}
